<template>
  <b-modal
    modal-class="component-modal-customer"
    ref="CustomerModal"
    size="xl fluid"
    :hide-footer="!isManagerOrCounter || readOnly"
    no-close-on-backdrop
    @hidden="close"
  >
    <template #modal-header="{ close }">
      <h5 class="modal-title">{{ $t('customer.modalTitle') }}</h5>
      <div class="d-flex ml-2 align-items-center">
        <b-button-group size="sm">
          <b-button
            :variant="currentLang === 'th' ? 'primary' : 'outline-secondary'"
            @click="switchLanguage('th')"
          >
            TH
          </b-button>
          <b-button
            :variant="currentLang === 'en' ? 'primary' : 'outline-secondary'"
            @click="switchLanguage('en')"
          >
            EN
          </b-button>
        </b-button-group>
      </div>
      <button
        type="button"
        aria-label="Close"
        class="close"
        @click="close()"
      >×</button>
    </template>
    <b-form
      ref="newPatientForm"
      id="newPatientForm"
      autocomplete="off"
      @submit.prevent="debouncedSavePatient"
    >
      <b-row>
        <b-col cols="12" sm="3" lg="2" class="divider-right">
          <b-row>
            <b-col cols="6" sm="12">
              <b-form-group class="mb-1" :label="$t('customer.dn')" label-class="pb-0">
                <b-form-input
                  size="sm"
                  type="text"
                  v-model="customer.dn"
                  :placeholder="$t('customer.dnAutoAssign')"
                  readonly
                />
              </b-form-group>
              <b-form-group
                :label="$t('customer.dnOld')"
                label-class="pb-0"
                :disabled="!isManagerOrCounter || readOnly"
              >
                <b-form-input size="sm" type="text" trim v-model="customer.existDn" />
              </b-form-group>
            </b-col>
            <b-col cols="6" sm="12">
              <b-form-group
                :label="$t('customer.photo')"
                label-class="pb-0"
                description
                :disabled="!isManagerOrCounter"
              >
                <upload-avatar-form
                  ref="UploadAvatar"
                  class="d-inline-block"
                  square
                  border
                  size="119px"
                  :uploadMaxSize="180"
                  :default-id="defaultContentId"
                  @callback="uploadAvatarCallback"
                ></upload-avatar-form>
              </b-form-group>
            </b-col>
          </b-row>
          <hr class="d-sm-none mt-0" />
        </b-col>
        <b-col>
          <b-row>
            <b-col cols="12" lg="6" class="divider-right">
              <b-form-group
                :label="$t('customer.personalInfo')"
                description
                :disabled="!isManagerOrCounter || readOnly"
              >
                <b-form-row>
                  <SmartCard
                    class="col"
                    ref="smartcard"
                    @getReaderData="getReaderData"
                    @showDialogToast="showDialogToast"
                  >
                  </SmartCard>
                  <SmartCardMOI
                    class="col"
                    ref="smartcardMoi"
                    @getReaderData="getReaderData"
                    @showDialogToast="showDialogToast"
                  >
                  </SmartCardMOI>
                  <select
                    class="col ml-2 form-control form-control-sm"
                    v-model="customer.personalType"
                  >
                    <option
                      v-for="item in personalTypeList"
                      :key="item.val"
                      :value="item.val"
                    >
                      {{ item.label }}
                    </option>
                  </select>
                  <span>
                    <b-form-input
                      size="sm"
                      id="personalId"
                      class="col ml-2"
                      :class="{ 'is-invalid': $v.customer.personalId.$error }"
                      v-model="$v.customer.personalId.$model"
                      trim
                      type="text"
                      :placeholder="$t('customer.idCard')"
                      @change="debouncedCheckDuplicates"
                    ></b-form-input>
                    <div
                      v-if="$v.customer.personalId.$error"
                      class="description col text-danger"
                    >
                      {{ $t('customer.validation.invalid13Digits') }}
                    </div>
                  </span>

                  <b-popover
                    id="matched-pid-popover"
                    target="personalId"
                    triggers="manual"
                    variant="danger"
                  >
                    <template #title>
                      <b-button
                        @click="closePopover"
                        class="close"
                        aria-label="Close"
                      >
                        <span
                          class="float-right ml-2"
                          style="line-height: 0.8"
                          aria-hidden="true"
                          >&times;</span
                        > </b-button
                      >{{ $t('customer.validation.duplicateId') }}
                    </template>
                    <b-button
                      block
                      variant="danger"
                      @click="openSearchCustomerModal(customer.personalId)"
                      >{{ $t('customer.selectExistingPatient') }}</b-button
                    >
                  </b-popover>
                </b-form-row>
                <b-form-row class="mt-3">
                  <vue-typeahead-bootstrap
                    class="col-3 pl-0 pr-0"
                    input-class="form-control-sm"
                    v-model="customer.titleNameTh"
                    :data="getTitleNameList"
                    :serializer="s => s.label"
                    :placeholder="$t('customer.titleTh')"
                    ref="titleNameTh"
                    :minMatchingChars="0"
                    :showOnFocus="true"
                  />
                  <b-form-input
                    size="sm"
                    id="firstname-input"
                    data-testid="patient-firstname"
                    autofocus
                    class="col ml-2 required"
                    v-model="$v.customer.firstNameTh.$model"
                    trim
                    lazy
                    type="text"
                    :placeholder="$t('customer.firstNameTh')"
                    :class="{ 'is-invalid': $v.customer.firstNameTh.$error }"
                    @change="debouncedCheckDuplicates"
                  ></b-form-input>
                </b-form-row>

                <b-form-row class="mt-2">
                  <b-form-input
                    size="sm"
                    id="lastname-input"
                    data-testid="patient-lastname"
                    class="col required"
                    v-model="$v.customer.lastNameTh.$model"
                    trim
                    lazy
                    type="text"
                    :placeholder="$t('customer.lastNameTh')"
                    :class="{ 'is-invalid': $v.customer.lastNameTh.$error }"
                    @change="debouncedCheckDuplicates"
                  ></b-form-input>
                  <b-popover
                    id="matched-name-popover"
                    target="lastname-input"
                    triggers="manual"
                    placement="bottom"
                    variant="warning"
                  >
                    <template #title>
                      <b-button
                        @click="closePopover"
                        class="close"
                        aria-label="Close"
                      >
                        <span
                          class="float-right ml-2"
                          style="line-height: 0.8"
                          aria-hidden="true"
                          >&times;</span
                        > </b-button
                      >{{ $t('customer.validation.duplicateName') }}
                    </template>
                    <b-button
                      block
                      variant="warning"
                      @click="
                        openSearchCustomerModal(
                          customer.firstNameTh + ' ' + customer.lastNameTh
                        )
                      "
                      >{{ $t('customer.selectExistingPatient') }}</b-button
                    >
                  </b-popover>
                </b-form-row>
                <b-form-row class="mt-4">
                  <vue-typeahead-bootstrap
                    class="col-3 pl-0 pr-0"
                    input-class="form-control-sm"
                    v-model="customer.titleNameEn"
                    :data="getTitleNameEngList"
                    :serializer="s => s.label"
                    :placeholder="$t('customer.titleEn')"
                    ref="titleNameEng"
                    :minMatchingChars="0"
                    :showOnFocus="true"
                  />
                  <b-form-input
                    size="sm"
                    class="col ml-2"
                    v-model="customer.firstNameEn"
                    type="text"
                    :placeholder="$t('customer.firstNameEn')"
                  ></b-form-input>
                </b-form-row>
                <b-form-row class="mt-2">
                  <b-form-input
                    size="sm"
                    class="col"
                    v-model="customer.lastNameEn"
                    type="text"
                    :placeholder="$t('customer.lastNameEn')"
                  ></b-form-input>
                </b-form-row>
                <b-form-row class="mt-4">
                  <b-form-input
                    size="sm"
                    class="col"
                    v-model="customer.nickName"
                    type="text"
                    :placeholder="$t('customer.nickname')"
                  ></b-form-input>
                  <vue-typeahead-bootstrap
                    class="col pl-0 pr-0 ml-2"
                    input-class="form-control-sm"
                    v-model="customer.gender"
                    :data="getGenderList"
                    :serializer="s => s.label"
                    :placeholder="$t('customer.gender')"
                    ref="gender"
                    :min-matching-chars="0"
                    :showOnFocus="true"
                  />
                </b-form-row>
                <b-form-row class="mt-2">
                  <datetime
                    class="col p-0"
                    input-class="form-control"
                    v-model="rawDob"
                    format="cccc dd LLLL yyyy"
                    :max-datetime="now"
                    value-zone="UTC+7"
                    auto
                    :placeholder="$t('customer.dob')"
                    :flow="['year', 'month', 'date']"
                  >
                  </datetime>
                </b-form-row>
                <b-form-row class="mt-4">
                  <b-form-input
                    size="sm"
                    class="col required"
                    data-testid="patient-allergic"
                    v-model="$v.customer.allergic.$model"
                    type="text"
                    :placeholder="$t('customer.allergies')"
                    :class="{ 'is-invalid': $v.customer.allergic.$error }"
                  ></b-form-input>
                </b-form-row>
                <b-form-row class="mt-2">
                  <b-form-input
                    size="sm"
                    class="col"
                    v-model="customer.congenitalDisease"
                    type="text"
                    :placeholder="$t('customer.congenitalDisease')"
                  ></b-form-input>
                </b-form-row>

                <b-form-row class="mt-2">
                  <vue-typeahead-bootstrap
                    class="col p-0"
                    input-class="form-control-sm"
                    v-model="customer.medicationHistory"
                    :data="medicationOptions"
                    :placeholder="$t('customer.medicationHistory')"
                    :minMatchingChars="0"
                    :showOnFocus="true"
                    maxlength="500"
                  />
                </b-form-row>
                <b-form-row class="mt-4">
                  <select
                    class="form-control form-control-sm col"
                    v-model="customer.medicalInsuranceType"
                  >
                    <option :value="null" selected disabled>
                      {{ $t('customer.medicalInsurance') }}
                    </option>
                    <option value="government_employee">{{ $t('customer.insurance.government_employee') }}</option>
                    <option value="self_pay">{{ $t('customer.insurance.self_pay') }}</option>
                    <option value="social_security">{{ $t('customer.insurance.social_security') }}</option>
                    <option value="state_enterprise">{{ $t('customer.insurance.state_enterprise') }}</option>
                    <option value="universal_coverage">{{ $t('customer.insurance.universal_coverage') }}</option>
                    <option value="other">{{ $t('customer.insurance.other') }}</option>
                  </select>
                </b-form-row>
                <b-form-row class="mt-2">
                  <select class="form-control form-control-sm col" v-model="customer.doctorId">
                    <option value="null" selected disabled>{{ $t('customer.attendingDoctor') }}</option>
                    <option
                      v-for="val in doctorList"
                      :key="val.uid"
                      :value="val.uid"
                    >
                      {{ val.fullName }}
                    </option>
                  </select>
                </b-form-row>
                <b-form-row class="mt-2">
                  <multiselect
                    class="col p-0"
                    v-model="reqExtDocItems"
                    :options="reqExtDocList"
                    :multiple="true"
                    :placeholder="$t('customer.reqDocuments')"
                    track-by="label"
                    label="label"
                    :limit="2"
                    @input="selectReqExtDocItems"
                  ></multiselect>
                </b-form-row>
              </b-form-group>
            </b-col>
            <b-col cols="12" lg="6">
              <b-form-group
                :label="isManagerOrCounter ? $t('customer.contactDetails') : ''"
                description
                :disabled="!isManagerOrCounter || readOnly"
              >
                <div v-if="isManagerOrCounter">
                  <b-form-row>
                    <div class="col px-0 d-flex">
                      <CountryCodeSelect
                        v-model="mobileCountry"
                        @select="onMobileCountryChange"
                      />
                      <b-form-input
                        size="sm"
                        class="flex-grow-1 required"
                        data-testid="patient-mobile"
                        type="text"
                        :placeholder="$t('customer.mobilePlaceholder')"
                        :class="{ 'is-invalid': $v.customer.mobile.$error }"
                        @input="applyMobileNumber"
                        :value="dashedMobile"
                      ></b-form-input>
                    </div>
                  </b-form-row>
                  <b-form-row class="mt-2">
                    <div class="col px-0 d-flex">
                      <CountryCodeSelect
                        v-model="telCountry"
                        @select="onTelCountryChange"
                      />
                      <b-form-input
                        size="sm"
                        class="flex-grow-1"
                        type="text"
                        :placeholder="$t('customer.homePlaceholder')"
                        @input="applyTelNumber"
                        :value="dashedTel"
                      ></b-form-input>
                    </div>
                  </b-form-row>
                  <b-form-row class="mt-2">
                    <b-form-input
                      size="sm"
                      class="col"
                      v-model="customer.lineId"
                      type="text"
                      :placeholder="$t('customer.lineId')"
                    ></b-form-input>
                    <b-form-input
                      size="sm"
                      class="col ml-2"
                      v-model="customer.email"
                      type="email"
                      :placeholder="$t('customer.email')"
                    ></b-form-input>
                  </b-form-row>
                  <b-form-row class="mt-3">
                    <b-form-textarea
                      size="sm"
                      v-model="customer.address"
                      :placeholder="$t('customer.address')"
                      rows="2"
                      max-rows="6"
                    ></b-form-textarea>
                  </b-form-row>
                  <b-form-row class="mt-2">
                    <b-form-input
                      size="sm"
                      class="col"
                      v-model="customer.subDistrict"
                      type="text"
                      :placeholder="$t('customer.subDistrict')"
                    ></b-form-input>
                    <b-form-input
                      size="sm"
                      class="col ml-2"
                      v-model="customer.district"
                      type="text"
                      :placeholder="$t('customer.district')"
                    ></b-form-input>
                  </b-form-row>
                  <b-form-row class="mt-2">
                    <b-form-input
                      size="sm"
                      class="col"
                      v-model="customer.province"
                      type="text"
                      :placeholder="$t('customer.province')"
                    ></b-form-input>
                    <b-form-input
                      size="sm"
                      class="col ml-2"
                      v-model="customer.country"
                      type="text"
                      :placeholder="$t('customer.country')"
                    ></b-form-input>
                  </b-form-row>
                  <b-form-row class="mt-2">
                    <b-form-input
                      size="sm"
                      class="col"
                      v-model="customer.postcode"
                      type="text"
                      :placeholder="$t('customer.postcode')"
                    ></b-form-input>
                  </b-form-row>
                  <b-form-row class="mt-2">
                    <b-form-input
                      size="sm"
                      class="col"
                      v-model="customer.nationality"
                      type="text"
                      maxlength="20"
                      :placeholder="$t('customer.nationality')"
                    ></b-form-input>
                  </b-form-row>
                </div>
                <b-form-row class="mt-3">
                  <b-form-select
                    size="sm"
                    v-model="customer.referralSources"
                    :options="sourceOptions"
                    multiple
                    :select-size="3"
                  ></b-form-select>
                </b-form-row>

                <b-form-row class="mt-3">
                  <b-form-textarea
                    size="sm"
                    v-model="customer.note"
                    :placeholder="$t('customer.additionalNotes')"
                    rows="2"
                    max-rows="6"
                  ></b-form-textarea>
                </b-form-row>
              </b-form-group>

              <b-form-group
                :label="$t('customer.emergencyContact')"
                description
                :disabled="!isManagerOrCounter || readOnly"
              >
                <b-form-row >
                  <b-form-input
                    size="sm"
                    v-model="customer.emergencyContactName"
                    type="text"
                    :placeholder="$t('customer.emergencyName')"
                    maxlength="100"
                  ></b-form-input>
                </b-form-row>
                <b-form-row class="mt-2">
                  <vue-typeahead-bootstrap
                    class="col pr-0 pl-0"
                    input-class="form-control-sm"
                    v-model="customer.emergencyContactRelationship"
                    :data="translatedRelationshipOptions"
                    :placeholder="$t('customer.emergencyRelationship')"
                    :minMatchingChars="0"
                    :showOnFocus="true"
                    maxlength="100"
                  />
                </b-form-row>
                <b-form-row class="mt-2">
                  <div class="col-12 px-0 d-flex">
                    <CountryCodeSelect
                      v-model="emergencyPhoneCountry"
                      @select="onEmergencyCountryChange"
                    />
                    <b-form-input
                      size="sm"
                      class="flex-grow-1"
                      type="tel"
                      :placeholder="$t('customer.emergencyPhone')"
                      @input="applyEmergencyPhone"
                      :value="dashedEmergencyPhone"
                    ></b-form-input>
                  </div>
                </b-form-row>
              </b-form-group>

              <b-form-group
                label="กลุ่มผู้ป่วย"
                description
                :disabled="!isManagerOrCounter || readOnly"
              >
                <PatientGroupPicker
                  v-model="customer.groupIds"
                  :clinic-url="$route.params.clinicUrl"
                  :branch-url="$route.params.branchUrl"
                  :small="true"
                  :exclude-managed="true"
                  @input="groupIdsTouched = true"
                />
                <!--
                  A tier-managed group can never be picked here (excludeManaged
                  above), so a 409 from the membership guard can't take down a
                  patient save. But a patient who ALREADY belongs to one must
                  still see that membership -- the picker's chips can't show it
                  since the group is filtered out of its options, so surface it
                  read-only instead of letting it look like the patient lost
                  the group.
                -->
                <small v-if="tierManagedGroupNames.length" class="text-muted d-block mt-1">
                  <i class="fas fa-lock mr-1"></i>ระบบระดับลูกค้าจัดการสมาชิกกลุ่มนี้ให้อัตโนมัติ:
                  {{ tierManagedGroupNames.join(', ') }}
                </small>
              </b-form-group>

              <b-form-checkbox
                v-if="branchLinkedWithKtbPaotang"
                v-model="customer.linkedWithKtbPaotang"
                switch
                style="font-weight: 400"
              >
                {{ $t('customer.paotangLink') }}
                <img
                  class="mr-1"
                  style="width: auto; height: 20px"
                  :src="PaotangLogo"
                />
              </b-form-checkbox>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-form>
    <template v-slot:modal-footer>
      <!-- <b-button form="newPatientForm" @click="resetButton" variant="danger"
        >รีเซ็ต</b-button
      >-->
      <b-button type="submit" form="newPatientForm" variant="primary" data-testid="patient-save"
        >{{ customer.id ? $t('customer.editPatient') : $t('customer.createPatient') }}
      </b-button>
    </template>
    <Loading v-if="isLoading"></Loading>
    <Dialog ref="Dialog"></Dialog>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import SmartCard from "@/components/SmartCard";
import SmartCardMOI from "@/components/SmartCardMOI";
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog";
import UploadAvatarForm from "@/components/form/UploadAvatar";
import PatientGroupPicker from "@/components/discount/PatientGroupPicker.vue";
import { getErrorMessage } from "@/constants/errorMessages";
import { tierManagedMemberNames } from "@/utils/patientGroupFilters";
import PaotangLogo from "../../assets/img/Paotang-small.png";
import debounce from "@/utils/debounce";
import i18n from "@/i18n";

import CountryCodeSelect from "@/components/common/CountryCodeSelect";
import { findCountryByCode } from "@/data/countryCodes";
import {
  parsePhoneNumber,
  formatPhoneForStorage,
  formatLocalInput,
  getValidationRules,
} from "@/utils/phoneUtils";

import { eventBus } from "@/main";

import {
  required,
  requiredIf,
  numeric,
  between,
  sameAs,
  not,
  helpers,
  email,
} from "vuelidate/lib/validators";

const digits = number =>
  helpers.withParams(
    { type: "digits", value: number },
    value => !helpers.req(value) || helpers.len(value) == number
  );

const validateThaiID = id => {
  // ตรวจสอบว่าเลขที่กรอกเข้ามามีความยาว 13 หลัก
  if (!/^\d{13}$/.test(id)) return false;

  // แปลง string ของ id ให้เป็น array ของตัวเลข
  let digits = id.split("").map(Number);

  // คำนวณผลรวมจากตัวเลข 12 ตัวแรก โดยใช้สูตร 13 - (ลำดับของตัวเลข)
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += digits[i] * (13 - i);
  }

  // คำนวณ check digit โดยเอา 11 ลบด้วยผลลัพธ์ของ sum mod 11 แล้ว mod 10 อีกครั้ง
  let checkDigit = (11 - (sum % 11)) % 10;

  // ตรวจสอบว่า check digit ตรงกับเลขตัวสุดท้ายของบัตรประชาชนหรือไม่
  return checkDigit === digits[12];
};

export default {
  name: "CustomerModal",
  i18n,
  components: {
    SmartCard,
    SmartCardMOI,
    Loading,
    Dialog,
    UploadAvatarForm,
    CountryCodeSelect,
    PatientGroupPicker,
  },
  data() {
    return {
      isLoading: false,
      medicationOptions: [
        // ยาต้านการแข็งตัวของเลือด (Anticoagulants/Antiplatelets)
        "Aspirin (แอสไพริน)",
        "Warfarin (วาร์ฟาริน)",
        "Clopidogrel (โคลพิโดเกรล)",
        "Heparin (เฮปาริน)",
        "Rivaroxaban (ริวารอกซาบัน)",
        // ยาเบาหวาน (Diabetes medications)
        "Metformin (เมตฟอร์มิน)",
        "Insulin (อินซูลิน)",
        "Glipizide (กลิพิไซด์)",
        "Gliclazide (กลิคลาไซด์)",
        // ยาความดันโลหิต (Antihypertensives)
        "Amlodipine (แอมโลดิพีน)",
        "Atenolol (อะทีโนลอล)",
        "Losartan (โลซาร์แทน)",
        "Enalapril (เอนาลาพริล)",
        "Nifedipine (นิเฟดิพีน)",
        // ยาต้านการสูญเสียมวลกระดูก (Bisphosphonates)
        "Bisphosphonates (บิสฟอสโฟเนต)",
        "Alendronate (อะเลนโดรเนต)",
        "Zoledronic acid (โซเลดรอนิก แอซิด)",
        // ยาแก้ปวด/ลดอักเสบ (Pain relievers/NSAIDs)
        "Ibuprofen (ไอบูโพรเฟน)",
        "Paracetamol (พาราเซตามอล)",
        "Diclofenac (ไดโคลฟีแนค)",
        "Naproxen (นาพรอกเซน)",
        // ยาปฏิชีวนะ (Antibiotics)
        "Amoxicillin (อะม็อกซีซิลลิน)",
        "Clindamycin (คลินดามัยซิน)",
        "Metronidazole (เมโทรนิดาโซล)",
        "Azithromycin (อะซิโทรมัยซิน)",
        // ยาสเตียรอยด์ (Steroids)
        "Prednisolone (เพรดนิโซโลน)",
        "Dexamethasone (เด็กซาเมทาโซน)",
        // ยาอื่นๆ
        "Levothyroxine (ลีโวไทร็อกซีน)",
        "Simvastatin (ซิมวาสแทติน)",
        "Omeprazole (โอเมพราโซล)",
      ],
      customer: {
        id: null,
        personalType: 1,
        personalId: null,
        dn: null,
        titleNameTh: null,
        firstNameTh: null,
        lastNameTh: null,
        titleNameEn: null,
        firstNameEn: null,
        lastNameEn: null,
        nickName: null,
        gender: null,
        mobile: null,
        email: null,
        lineId: null,
        dob: null,
        tel: null,
        congenitalDisease: null,
        allergic: null,
        medicationHistory: null,
        medicalInsuranceType: null,
        emergencyContactName: null,
        emergencyContactRelationship: null,
        emergencyContactPhone: null,
        reqDocItems: [],
        note: null,
        contentId: null,
        doctorId: null,
        address: null,
        subDistrict: null,
        district: null,
        province: null,
        postcode: null,
        country: null,
        nationality: null,
        existDn: null,
        linkedWithKtbPaotang: false,
        referralSources: [],
        groupIds: undefined,
      },
      groupIdsTouched: false,
      rawDob: null,
      reqExtDocItems: [],
      appointment: null,
      defaultContentId: null,

      isAutoAssignDn: true,
      readOnly: false,
      openFrom: null,

      dashedMobile: "",
      dashedTel: "",
      dashedEmergencyPhone: "",
      mobileCountry: "TH",
      telCountry: "TH",
      emergencyPhoneCountry: "TH",
      PaotangLogo,
      debouncedSavePatient: null,
      debouncedCheckDuplicates: null,
    };
  },
  validations() {
    if (
      (this.customer.personalId && this.customer.personalType === 1) ||
      (this.customer.linkedWithKtbPaotang && this.branchLinkedWithKtbPaotang)
    ) {
      return {
        customer: {
          personalId: { required, numeric, validateThaiID },
          firstNameTh: { required },
          lastNameTh: { required },
          allergic: { required },
          mobile: {
            required,
            validPhone: (value) => {
              if (!value) return true;
              const parsed = parsePhoneNumber(value);
              const rules = getValidationRules(parsed.countryCode);
              const digits = parsed.nationalNumber.replace(/\D/g, "");
              return digits.length >= rules.min && digits.length <= rules.max;
            },
          },
        },
      };
    } else {
      return {
        customer: {
          personalId: {},
          firstNameTh: { required },
          lastNameTh: { required },
          allergic: { required },
          mobile: {
            required,
            validPhone: (value) => {
              if (!value) return true;
              const parsed = parsePhoneNumber(value);
              const rules = getValidationRules(parsed.countryCode);
              const digits = parsed.nationalNumber.replace(/\D/g, "");
              return digits.length >= rules.min && digits.length <= rules.max;
            },
          },
        },
      };
    }
  },
  created() {
    this.init();
  },
  mounted() {
    this.debouncedSavePatient = debounce(this.savePatient);
    this.debouncedCheckDuplicates = debounce(this.checkDupicates);
  },
  computed: {
    ...mapGetters({
      getUserInfo: "moduleUser/getUserInfo",
      getGenderList: "moduleMaster/getGenderList",
      getTitleNameList: "moduleMaster/getTitleNameList",
      getTitleNameEngList: "moduleMaster/getTitleNameEngList",
      personalTypeList: "moduleMaster/getPersonalTypeList",
      reqExtDocList: "moduleMaster/getReqExtDocList",
      reqExtDocBy: "moduleMaster/getReqExtDocBy",
      doctorList: "moduleUser/getDoctorList",
      appointmentBy: "moduleAppointment/getQueueAppointmentBy",
      isAccessMenu: "moduleUser/getIsAccessMenu",
      getBranchInfo: "moduleUser/getBranchInfo",
      branchLinkedWithKtbPaotang: "moduleUser/getLinkedWithKtbPaotang",
      getGroupList: "modulePatientGroup/getGroupList",
    }),
    patientGroupOptions() {
      return (this.getGroupList || []).map(g => ({
        text: g.name,
        value: g.id,
      }));
    },
    // Snapshot of the patient's tier-managed memberships as loaded by show().
    // Deliberately reads customer.groupMembers (untouched by the picker),
    // NOT customer.groupIds -- the picker's v-model gets fully replaced by
    // its own (managed-excluded) options on every @input, so deriving this
    // from groupIds would make the note disappear the moment the user picks
    // an unrelated group, which is exactly the "looks like it silently lost
    // the group" bug this note exists to prevent.
    tierManagedGroupNames() {
      const groupMembers = Array.isArray(this.customer.groupMembers)
        ? this.customer.groupMembers
        : [];
      return tierManagedMemberNames(groupMembers, this.getGroupList);
    },
    now() {
      return moment().format();
    },
    isManagerOrCounter() {
      return this.isAccessMenu(["COUNTER", "MANAGER", "HEADCOUNTER"]);
    },
    currentLang() {
      return this.$i18n.locale;
    },
    sourceOptions() {
      return [
        { value: null, text: this.$t('customer.referralSource'), disabled: true },
        { value: "facebook_ad", text: this.$t('customer.referralSources.facebook_ad') },
        { value: "google_ad", text: this.$t('customer.referralSources.google_ad') },
        { value: "instagram_ad", text: this.$t('customer.referralSources.instagram_ad') },
        { value: "tiktok_ad", text: this.$t('customer.referralSources.tiktok_ad') },
        { value: "youtube", text: this.$t('customer.referralSources.youtube') },
        { value: "website", text: this.$t('customer.referralSources.website') },
        { value: "friend_referral", text: this.$t('customer.referralSources.friend_referral') },
        { value: "doctor_referral", text: this.$t('customer.referralSources.doctor_referral') },
        { value: "walk_in", text: this.$t('customer.referralSources.walk_in') },
        { value: "printed_media", text: this.$t('customer.referralSources.printed_media') },
        { value: "event_expo", text: this.$t('customer.referralSources.event_expo') },
        { value: "other", text: this.$t('customer.referralSources.other') },
      ];
    },
    translatedRelationshipOptions() {
      return [
        this.$t('customer.relationships.father'),
        this.$t('customer.relationships.mother'),
        this.$t('customer.relationships.older_brother'),
        this.$t('customer.relationships.older_sister'),
        this.$t('customer.relationships.younger_brother'),
        this.$t('customer.relationships.younger_sister'),
        this.$t('customer.relationships.spouse'),
        this.$t('customer.relationships.husband'),
        this.$t('customer.relationships.wife'),
        this.$t('customer.relationships.child'),
        this.$t('customer.relationships.son'),
        this.$t('customer.relationships.daughter'),
        this.$t('customer.relationships.friend'),
        this.$t('customer.relationships.relative'),
        this.$t('customer.relationships.other'),
      ];
    },
    formattedDob() {
      if (this.rawDob) {
        const d = moment(this.rawDob).locale("th").format("YYYY-MM-DD");
        return d === "Invalid date" ? null : d;
      }
      return null;
    },
  },
  methods: {
    ...mapActions({
      createPatient: "modulePatient/createPatient",
      updatePatient: "modulePatient/updatePatient",
      updateStateQueueAppointmentPatient:
        "moduleAppointment/updateStateQueueAppointmentPatient",
      checkNamePatient: "modulePatient/checkNamePatient",
      checkDuplicate: "modulePatient/checkDuplicate",
      fetchGroupList: "modulePatientGroup/fetchGroupList",
    }),
    init() {
      const clinicUrl = this.$route?.params?.clinicUrl;
      const branchUrl = this.$route?.params?.branchUrl;
      if (clinicUrl && branchUrl) {
        this.fetchGroupList({ clinicUrl, branchUrl });
      }
    },
    switchLanguage(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem('customerFormLang', lang);
    },
    show(customer, appointment = null, readOnly = false, openFrom = null) {
      this.setDefultCustomer();
      this.appointment = appointment; //ห้ามแก้เป็น {...appointment} จะทำให้เกิด bug ตอนเปลี่ยนจาก unNameApt เป็น apt Why???
      this.readOnly = readOnly;
      this.openFrom = openFrom;

      this.$refs["CustomerModal"].show();
      if (customer) {
        // groupIds must be in the Object.assign payload so Vue 2 makes it reactive;
        // assigning it after replaces a non-reactive property and the picker won't sync.
        const groupIds = Array.isArray(customer.groupMembers) && customer.groupMembers.length > 0
          ? customer.groupMembers.map(m => m.groupId)
          : [];
        this.customer = Object.assign({}, customer, {
          referralSources: Array.isArray(customer.referralSources) ? customer.referralSources : [],
          groupIds,
        });
        this.groupIdsTouched = false;
        this.rawDob = this.customer.dob;
        // Parse phone numbers and set country dropdowns
        if (customer.mobile) {
          const parsed = parsePhoneNumber(customer.mobile);
          this.mobileCountry = parsed.countryCode;
          this.customer.mobile = formatPhoneForStorage(
            parsed.dialCode,
            parsed.nationalNumber
          );
          this.dashedMobile = formatLocalInput(
            parsed.nationalNumber,
            parsed.countryCode
          );
        }
        if (customer.tel) {
          const parsed = parsePhoneNumber(customer.tel);
          this.telCountry = parsed.countryCode;
          this.customer.tel = formatPhoneForStorage(
            parsed.dialCode,
            parsed.nationalNumber
          );
          this.dashedTel = formatLocalInput(
            parsed.nationalNumber,
            parsed.countryCode
          );
        }
        if (customer.emergencyContactPhone) {
          const parsed = parsePhoneNumber(customer.emergencyContactPhone);
          this.emergencyPhoneCountry = parsed.countryCode;
          this.customer.emergencyContactPhone = formatPhoneForStorage(
            parsed.dialCode,
            parsed.nationalNumber
          );
          this.dashedEmergencyPhone = formatLocalInput(
            parsed.nationalNumber,
            parsed.countryCode
          );
        }
        this.reqExtDocItems = [];
        this.customer.reqDocItems?.forEach(item => {
          const reqExtDoc = this.reqExtDocBy(item, "val");
          this.reqExtDocItems.push(reqExtDoc);
        });
        this.defaultContentId = customer.contentId ? customer.contentId : null;
      } else {
        // this.setDefultCustomer();
      }
    },
    close() {
      this.setDefultCustomer();
      this.$v.$reset();
    },
    getReaderData(readerData) {
      if (readerData) {
        this.customer.personalType = 1;
        this.customer.personalId = readerData.Citizenid;
        this.customer.titleNameTh = readerData.Th_Prefix;
        this.customer.firstNameTh = readerData.Th_Firstname;
        this.customer.lastNameTh = readerData.Th_Lastname;
        this.customer.titleNameEn = readerData.En_Prefix;
        this.customer.firstNameEn = readerData.En_Firstname;
        this.customer.lastNameEn = readerData.En_Lastname;
        this.customer.gender = readerData.gender;
        this.rawDob = readerData.Birthday;
        this.customer.province = readerData.addrProvince;
        this.customer.district = readerData.addrAmphur;
        this.customer.subDistrict = readerData.addrTambol;
        this.customer.address = readerData.addrHouseNo;
        this.photoRawToFile(readerData.PhotoRaw);
      }
    },
    photoRawToFile(data) {
      var dataURI = "data:image/jpeg;base64," + data;
      this.$refs.UploadAvatar.setImage(
        dataURI,
        `cid-${this.customer.personalId}.jpg`
      );
    },
    uploadAvatarCallback(state, contentId) {
      switch (state) {
        case "uploading":
          this.isLoading = true;
          break;
        case "callback":
          this.isLoading = false;
          this.checkPatient();
          break;
        case "delete":
          this.customer.contentId = contentId;
          break;
        case "reset":
          this.customer.contentId = contentId;
          break;
        case "success":
          this.isLoading = false;
          this.customer.contentId = contentId;
          this.checkPatient();
          break;
        case "fail":
          this.isLoading = false;
          this.showDialogToast("danger", this.$t('customer.messages.uploadFailed'));
          this.checkPatient();
          break;
        default:
      }
    },
    resetForm() {
      this.$refs.UploadAvatar.reset("");
      this.setDefultCustomer();
      this.$v.$reset();
    },
    savePatient() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.isLoading = true;
      this.$refs.UploadAvatar.submit();
    },
    async checkPatient() {
      let isDismissed;
      if (
        this.getBranchInfo.linkedWithNhso &&
        this.customer.personalType === 1 &&
        this.customer.personalId?.length !== 13
      ) {
        await this.$refs.Dialog.showAlertConfirm(
          "คุณไม่ได้กรอกเลขบัตรประชาชน",
          "ต้องการสร้างคนไข้ใหม่ โดยไม่มีเลขบัตรประชาชน",
          "warning",
          "สร้างคนไข้ใหม่โดยไม่มีเลขบัตร",
          "กลับไปเพิ่มเลขบัตร"
        ).then(res => {
          isDismissed = res.isDismissed;
        });
      }
      if (isDismissed) return;

      this.isLoading = true;
      let matched = await this.checkDupicatePatients();
      this.isLoading = false;

      if (matched && matched.duplicateBy === "PID") {
        this.$refs.Dialog.showAlertInfo(
          "เลขประจำตัวซ้ำ กับคนไข้เดิม",
          "เลือกจากคนไข้เดิม",
          "warning"
        ).then(() => {
          this.openSearchCustomerModal(this.customer.personalId);
          this.isLoading = false;
          return;
        });
      } else if (
        matched &&
        matched.duplicateBy === "NAME" &&
        !this.customer.id
      ) {
        this.$refs.Dialog.showAlertConfirm(
          "ชื่อ และนามสกุลซ้ำกับคนไข้ที่เดิม",
          "ต้องการสร้างคนไข้ซ้ำ?",
          "warning",
          "สร้างคนไข้ชื่อซ้ำ",
          "เลือกจากคนไข้เดิม",
          true
        ).then(res => {
          if (res.isDismissed) {
            this.openSearchCustomerModal(
              this.customer.firstNameTh + " " + this.customer.lastNameTh
            );
            this.isLoading = false;
            return;
          } else {
            this.saveCustomer();
          }
        });
      } else {
        this.saveCustomer();
      }
    },
    buildCustomerPayload() {
      const payload = {
        ...this.customer,
        dob: this.formattedDob,
      };
      // Only send groupIds if user touched the group picker
      if (!this.groupIdsTouched) {
        delete payload.groupIds;
      } else {
        payload.groupIds = payload.groupIds || [];
      }
      return payload;
    },
    saveCustomer() {
      this.isLoading = true;

      if (!this.customer.id) {
        this.createPatient({
          customer: this.buildCustomerPayload(),
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        })
          .then(res => {
            this.isLoading = false;
            if (res.status == 200) {
              this.customer.dn = res.data.dn;
              this.customer.id = res.data.id;

              if (this.openFrom === "UnnamedPtApt") {
                eventBus.$emit("applyPt", this.customer);
                eventBus.$emit("alertToast", {
                  message: "สร้างคนไข้สำเร็จ",
                  variant: "success",
                });
              } else if (this.openFrom === "QManage") {
                let event = {
                  id: parseInt(this.appointment.id),
                  startDt: this.appointment.start,
                  endDt: this.appointment.end,
                  roomId: parseInt(this.appointment.extendedProps.roomId),
                  aptTreatItems: this.appointment.extendedProps.treatmentList,
                  rmdId: this.appointment.extendedProps.rmdId,
                  doctorUid: this.appointment.extendedProps.doctor,
                  note: this.appointment.extendedProps.note,
                  patientId: this.customer.id,
                };
                this.$store.dispatch(
                  "moduleAppointment/updateQueueAppointment",
                  {
                    event,
                    clinicUrl: this.$route.params.clinicUrl,
                    branchUrl: this.$route.params.branchUrl,
                  }
                );
              } else {
                this.$emit(
                  "openProfileCustomerModal",
                  Object.assign({}, this.appointment),
                  Object.assign({}, this.customer)
                );
              }

              this.$refs["CustomerModal"].hide();
            }
          })
          .catch(res => {
            this.isLoading = false;
            if (res.response?.data?.code === "PAT0001") {
              this.$root.$emit("bv::show::tooltip", "personalId");
              this.$refs.Dialog.showAlertInfo(
                "เลขบัตรประชาชน/Passport ซ้ำ",
                "ไม่สามารถสร้างประวัติคนไข้ใหม่ได้",
                "warning"
              ).then(() => {
                setTimeout(
                  () => this.$root.$emit("bv::hide::tooltip", "personalId"),
                  8000
                );
              });
            } else {
              // This API never returns a `message` key -- reading
              // res.response.data.message here would silently resolve to
              // undefined. getErrorMessage reads the actual errorMessage
              // field (see src/constants/errorMessages.js).
              this.$refs.Dialog.showToast("danger", getErrorMessage(res));
            }
          });
      } else {
        this.$refs.Dialog.showAlertConfirm(
          "แก้ไขข้อมูลคนไข้ ?",
          "คุณต้องการแก้ไข รายละเอียดข้อมูลคนไข้"
        ).then(res => {
          if (res.isConfirmed) {
            this.updatePatient({
              customer: this.buildCustomerPayload(),
              clinicUrl: this.$route.params.clinicUrl,
              branchUrl: this.$route.params.branchUrl,
            })
              .then(res => {
                if (res.status == "200") {
                  this.$refs["CustomerModal"].hide();
                  this.customer = res.data;
                  let appointment = Object.assign({}, this.appointment);
                  let patient = Object.assign({}, this.customer);
                  if (appointment && patient) {
                    this.updateStateQueueAppointmentPatient({
                      id: appointment.id,
                      patient: patient,
                    });
                  }
                  eventBus.$emit("updatePatientInfo");

                  // this.$emit("openProfileCustomerModal", appointment, patient);
                }
                this.isLoading = false;
              })
              .catch(err => {
                console.error(err);
                this.isLoading = false;
                // Same field-name pitfall as createPatient's catch above --
                // this API never returns `message`, only `errorMessage`.
                this.$refs.Dialog.showToast("danger", getErrorMessage(err));
              });
          } else {
            this.isLoading = false;
          }
        });
      }
    },
    async checkDupicates() {
      let matched = await this.checkDupicatePatients();
      if (matched && matched.duplicateBy === "PID") {
        this.$root.$emit("bv::show::popover", "matched-pid-popover");
      } else if (matched && matched.duplicateBy === "NAME") {
        this.$root.$emit("bv::show::popover", "matched-name-popover");
      }
    },
    checkDupicatePatients() {
      let { personalId, firstNameTh, lastNameTh } = this.customer;
      if (!personalId && !firstNameTh && !lastNameTh) return;
      if (this.customer.personalId === "") {
        this.customer.personalId = null;
      }
      if (this.customer.firstNameTh === "") {
        this.customer.firstNameTh = null;
      }
      if (this.customer.lastNameTh === "") {
        this.customer.lastNameTh = null;
      }
      return this.checkDuplicate({
        customer: this.customer,
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      }).then(res => {
        let dupData = res.data;
        if (dupData?.result?.length > 0) {
          return res.data;
        } else {
          this.closePopover();
          return false;
        }
      });
    },
    closePopover() {
      this.$root.$emit("bv::hide::popover");
    },
    customLabel(option) {
      return `${option.label}`;
    },
    selectReqExtDocItems() {
      let reqExtDocItems = [];

      this.reqExtDocItems.forEach(element => {
        reqExtDocItems.push(element.val);
      });
      this.customer.reqDocItems = reqExtDocItems;
    },
    resetButton() {
      this.$refs.Dialog.showAlertConfirm("รีเซ็ตข้อมูล ?").then(result => {
        if (!result.value) {
          return;
        }
      });
      this.setDefultCustomer();
    },
    setDefultCustomer() {
      this.customer = Object.assign(
        {},
        {
          personalType: 1,
          personalId: null,
          dn: null,
          titleNameTh: null,
          firstNameTh: null,
          lastNameTh: null,
          titleNameEn: null,
          firstNameEn: null,
          lastNameEn: null,
          nickName: null,
          gender: null,
          mobile: null,
          email: null,
          lineId: null,
          dob: null,
          tel: null,
          congenitalDisease: null,
          allergic: null,
          medicationHistory: null,
          medicalInsuranceType: null,
          emergencyContactName: null,
          emergencyContactRelationship: null,
          emergencyContactPhone: null,
          reqDocItems: [],
          note: null,
          contentId: null,
          doctorId: null,
          address: null,
          subDistrict: null,
          district: null,
          province: null,
          postcode: null,
          country: null,
          nationality: null,
          existDn: null,
          linkedWithKtbPaotang: false,
          referralSources: [],
          groupIds: undefined,
        }
      );
      this.groupIdsTouched = false;
      this.isLoading = false;
      this.reqExtDocItems = [];
      this.appointment = null;
      this.defaultContentId = null;
      this.dashedMobile = "";
      this.dashedTel = "";
      this.dashedEmergencyPhone = "";
      this.mobileCountry = "TH";
      this.telCountry = "TH";
      this.emergencyPhoneCountry = "TH";
      this.rawDob = null;
    },
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    openSearchCustomerModal(val) {
      this.$refs["CustomerModal"].hide();
      this.$parent.openSearchCustomerModal(val);
    },
    applyMobileNumber(e) {
      if (!e) return;
      const num = e.replace(/[^0-9]/g, "");
      const countryData = findCountryByCode(this.mobileCountry);
      this.$v.customer.mobile.$model = formatPhoneForStorage(countryData.dialCode, num);
      this.dashedMobile = formatLocalInput(num, this.mobileCountry);
    },
    applyTelNumber(e) {
      if (!e) return;
      const num = e.replace(/[^0-9]/g, "");
      const countryData = findCountryByCode(this.telCountry);
      this.customer.tel = formatPhoneForStorage(countryData.dialCode, num);
      this.dashedTel = formatLocalInput(num, this.telCountry);
    },
    applyEmergencyPhone(e) {
      if (!e) return;
      const num = e.replace(/[^0-9]/g, "");
      const countryData = findCountryByCode(this.emergencyPhoneCountry);
      this.customer.emergencyContactPhone = formatPhoneForStorage(countryData.dialCode, num);
      this.dashedEmergencyPhone = formatLocalInput(num, this.emergencyPhoneCountry);
    },
    onMobileCountryChange(country) {
      if (this.customer.mobile) {
        const parsed = parsePhoneNumber(this.customer.mobile);
        this.customer.mobile = formatPhoneForStorage(country.dialCode, parsed.nationalNumber);
        this.dashedMobile = formatLocalInput(parsed.nationalNumber, country.code);
      }
    },
    onTelCountryChange(country) {
      if (this.customer.tel) {
        const parsed = parsePhoneNumber(this.customer.tel);
        this.customer.tel = formatPhoneForStorage(country.dialCode, parsed.nationalNumber);
        this.dashedTel = formatLocalInput(parsed.nationalNumber, country.code);
      }
    },
    onEmergencyCountryChange(country) {
      if (this.customer.emergencyContactPhone) {
        const parsed = parsePhoneNumber(this.customer.emergencyContactPhone);
        this.customer.emergencyContactPhone = formatPhoneForStorage(country.dialCode, parsed.nationalNumber);
        this.dashedEmergencyPhone = formatLocalInput(parsed.nationalNumber, country.code);
      }
    },
  },
};
</script>

<style scoped>
.required {
  background-color: #ccecff6c;
}

.form-row {
  margin-left: 0;
  margin-right: 0;
}

/* Make all form controls smaller - reduce height, padding, line-height */
.form-control-sm,
.form-control.form-control-sm {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

/* Target regular form-control inputs with size="sm" */
.form-control[size="sm"] {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

/* Make textareas smaller */
textarea.form-control-sm {
  min-height: auto !important;
  padding: 0.25rem 0.5rem !important;
}

/* Make select dropdowns smaller */
select.form-control-sm {
  height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
}

/* Make datetime picker input smaller */
::v-deep .vdatetime-input {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

::v-deep .vdatetime input.form-control {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

::v-deep .vdatetime .form-control {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

/* Make vue-typeahead-bootstrap smaller */
::v-deep .vue-typeahead-bootstrap input.form-control-sm {
  height: 31px !important;
  min-height: 31px !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

::v-deep .vue-typeahead-bootstrap .dropdown-item {
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
}

/* Make multiselect component smaller */
::v-deep .multiselect {
  min-height: 31px !important;
  font-size: 0.875rem !important;
}

::v-deep .multiselect__tags {
  min-height: 31px !important;
  padding: 3px 40px 0 4px !important;
  font-size: 0.875rem !important;
}

::v-deep .multiselect__input,
::v-deep .multiselect__single {
  font-size: 0.875rem !important;
  padding: 0 0 0 3px !important;
  margin-bottom: 3px !important;
  min-height: 20px !important;
  line-height: 20px !important;
}

::v-deep .multiselect__select {
  height: 31px !important;
}

::v-deep .multiselect__option {
  padding: 6px 12px !important;
  min-height: 28px !important;
  font-size: 0.875rem !important;
  line-height: 1.3 !important;
}

::v-deep .multiselect__tag {
  padding: 2px 22px 2px 6px !important;
  margin-bottom: 2px !important;
  font-size: 0.75rem !important;
  line-height: 1.3 !important;
}

::v-deep .multiselect__tag-icon {
  line-height: 18px !important;
  width: 18px !important;
}

::v-deep .multiselect__tag-icon:after {
  line-height: 18px !important;
}

::v-deep .multiselect__placeholder {
  font-size: 0.875rem !important;
  padding-top: 2px !important;
  margin-bottom: 3px !important;
}

/* Phone input next to country code dropdown - remove left border-radius */
.country-code-select + .form-control,
.country-code-select + .form-control-sm {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>